import React, { useState, useEffect } from 'react';
import { FiEye } from 'react-icons/fi';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { apiClient } from '../utils/api';

const Events = ({ user, onLogout }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchEvents = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit };
      const response = await apiClient.get('events', { params });
      
      if (response.data && response.data.data) {
        setEvents(response.data.data);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages || Math.ceil(response.data.total / limit));
        setCurrentPage(response.data.page);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async (id) => {
    setModalLoading(true);
    try {
      const response = await apiClient.get(`events/${id}`);
      console.log(response.data);
      if (response.data && response.data) {
        console.log('Setting selected event:', response.data);
        setSelectedEvent(response.data);
      } else {
        console.log('No data found in response');
      }
    } catch (err) {
      console.error('Error fetching event details:', err);
    } finally {
      setModalLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchEvents(page);
  };

  const handleViewEvent = async (id) => {
    try {
      console.log('Opening event modal for ID:', id);
      await fetchEventDetails(id);
      console.log('Setting showModal to true');
      setShowModal(true);
    } catch (error) {
      console.error('Error opening event modal:', error);
    }
  };

  const handleColumnFilter = (key, value) => {
    // Handle column filtering if needed
  };

  const handleSort = (sortConfig) => {
    // Handle sorting if needed
  };

  const handleRowAction = (actionKey, row, index) => {
    // Handle row actions if needed
  };

  const handleRowSelect = (selectedRows) => {
    // Handle row selection if needed
  };

  const handlePageSizeChange = (newPageSize) => {
    // Handle page size change if needed
  };

  const closeModal = () => {
    console.log('Closing modal, clearing event data');
    setShowModal(false);
    setSelectedEvent(null);
  };

  useEffect(() => {
    fetchEvents(1);
  }, []);

  const columns = [
    { 
      key: 'title', 
      title: 'Event Title', 
      render: (value, row) => (
        <span className="text-sm font-medium text-gray-900">{row.title}</span>
      )
    },
    { 
      key: 'company', 
      title: 'Company', 
      render: (value, row) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 rounded-full overflow-hidden">
            <img 
              src={row.company?.image} 
              alt={row.company?.name} 
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{row.company?.name}</p>
            <p className="text-xs text-gray-500">{row.company?.email}</p>
          </div>
        </div>
      )
    },
    { 
      key: 'location', 
      title: 'Location', 
      render: (value, row) => (
        <span className="text-sm text-gray-900">{row.location}</span>
      )
    },
    { 
      key: 'dates', 
      title: 'Event Dates', 
      render: (value, row) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{row.start_date}</p>
          <p className="text-xs text-gray-500">to {row.end_date}</p>
        </div>
      )
    },
    { 
      key: 'time', 
      title: 'Time', 
      render: (value, row) => (
        <span className="text-sm text-gray-900">{row.time}</span>
      )
    },
    { 
      key: 'state', 
      title: 'Status', 
      render: (value, row) => {
        const stateColors = {
          processing: 'bg-yellow-100 text-yellow-800',
          active: 'bg-green-100 text-green-800',
          cancelled: 'bg-red-100 text-red-800',
          completed: 'bg-blue-100 text-blue-800'
        };
        
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${stateColors[row.state] || 'bg-gray-100 text-gray-800'}`}>
            {row.state_label}
          </span>
        );
      }
    },
    { 
      key: 'actions', 
      title: 'Actions', 
      render: (value, row) => (
        <button
          onClick={() => handleViewEvent(row.id)}
          className="text-primary-600 hover:text-primary-900 p-1 rounded-full hover:bg-primary-50"
          title="View Details"
        >
          <FiEye className="h-4 w-4" />
        </button>
      )
    },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader title="Events" description="View and manage event information" user={user} />
        
        {/* Events Table */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Events List</h2>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
              </div>
            ) : (
              <DataTable
                data={events}
                columns={columns}
                loading={loading}
                emptyMessage="No events found"
                pagination={{ 
                  current: currentPage, 
                  total: total, 
                  pageSize: limit, 
                  totalPages: totalPages 
                }}
                onSort={handleSort}
                onFilter={handleColumnFilter}
                onRowAction={handleRowAction}
                onRowSelect={handleRowSelect}
                onPageSizeChange={handlePageSizeChange}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>

        {/* Event Details Modal */}
        {console.log('Modal state:', { showModal, selectedEvent, modalLoading })}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Event Details
                      </h3>
                      
                      {modalLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                        </div>
                      ) : selectedEvent ? (
                        <div className="space-y-6">
                          {/* Event Header */}
                          <div className="border-b pb-4">
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">{selectedEvent.title}</h4>
                            <p className="text-gray-600">{selectedEvent.description}</p>
                          </div>

                          {/* Company Info */}
                          <div className="border-b pb-4">
                            <h4 className="text-sm font-medium text-gray-500 mb-2">Organizing Company</h4>
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 rounded-full overflow-hidden">
                                <img 
                                  src={selectedEvent.company?.image} 
                                  alt={selectedEvent.company?.name} 
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.company?.name}</p>
                                <p className="text-xs text-gray-500">{selectedEvent.company?.email}</p>
                              </div>
                            </div>
                          </div>

                          {/* Event Details Grid */}
                          <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs text-gray-500">Location</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.location}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Time</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.time}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Start Date</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.start_date}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">End Date</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.end_date}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Number of Dates</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.number_of_dates}</p>
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs text-gray-500">Max Students</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.max_required_students}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Student Fee</p>
                                <p className="text-sm font-medium text-gray-900">${selectedEvent.amount_for_student}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Rating</p>
                                <p className="text-sm font-medium text-gray-900">{selectedEvent.rate}/5</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Status</p>
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  selectedEvent.state === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                                  selectedEvent.state === 'active' ? 'bg-green-100 text-green-800' :
                                  selectedEvent.state === 'cancelled' ? 'bg-red-100 text-red-800' :
                                  selectedEvent.state === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {selectedEvent.state_label}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Event Requests Summary */}
                          {selectedEvent.event_requests && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-500 mb-2">Event Requests</h4>
                              <div className="grid grid-cols-3 gap-4">
                                <div className="text-center">
                                  <p className="text-2xl font-bold text-green-600">{selectedEvent.event_requests.accepted}</p>
                                  <p className="text-xs text-gray-500">Accepted</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-2xl font-bold text-yellow-600">{selectedEvent.event_requests.pending}</p>
                                  <p className="text-xs text-gray-500">Pending</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-2xl font-bold text-red-600">{selectedEvent.event_requests.rejected}</p>
                                  <p className="text-xs text-gray-500">Rejected</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Agenda */}
                          {selectedEvent.agenda && selectedEvent.agenda.length > 0 && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-500 mb-2">Event Agenda</h4>
                              <div className="space-y-2">
                                {selectedEvent.agenda.map((item, index) => (
                                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                                      <p className="text-xs text-gray-500">
                                        {item.time_from} - {item.time_to}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Speakers */}
                          {selectedEvent.speakers && selectedEvent.speakers.length > 0 && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-500 mb-2">Speakers</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {selectedEvent.speakers.map((speaker) => (
                                  <div key={speaker.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                                    <div className="w-10 h-10 rounded-full overflow-hidden">
                                      <img 
                                        src={speaker.image} 
                                        alt={speaker.name} 
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{speaker.name}</p>
                                      <p className="text-xs text-gray-500">{speaker.title}</p>
                                      <p className="text-xs text-gray-500">{speaker.duration_in_minutes} min</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Interests */}
                          {selectedEvent.interests && selectedEvent.interests.length > 0 && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-500 mb-2">Related Interests</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedEvent.interests.map((interest) => (
                                  <span key={interest.id} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                    {interest.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Images */}
                          {selectedEvent.images && selectedEvent.images.length > 0 && (
                            <div className="border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-500 mb-2">Event Images</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                {selectedEvent.images.map((image) => (
                                  <div key={image.id} className="aspect-w-16 aspect-h-9">
                                    <img 
                                      src={image.image} 
                                      alt="Event" 
                                      className="w-full h-32 object-cover rounded-lg"
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Events;
